import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";

import "./UserAccountPage.css";

function getStoredUser() {
  try {
    return JSON.parse(secureLocalStorage.getItem("profile"));
  } catch {
    return null;
  }
}

function getInitials(firstName, lastName) {
  const first = firstName?.[0] || "";
  const last = lastName?.[0] || "";

  return `${first}${last}`.toUpperCase() || "U";
}

function InfoField({ label, value, icon }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.8 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 2,
            background: "#eff6ff",
            color: "#1d4ed8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontWeight: 900,
            color: "#334155",
            fontSize: 13,
          }}
        >
          {label}
        </Typography>
      </Stack>

      <TextField
        value={value || "—"}
        InputProps={{ readOnly: true }}
        fullWidth
        variant="outlined"
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            background: "#f8fafc",
            fontWeight: 700,
            color: "#0f172a",

            "& fieldset": {
              borderColor: "#e2e8f0",
            },

            "&:hover fieldset": {
              borderColor: "#bfdbfe",
            },

            "&.Mui-focused fieldset": {
              borderColor: "#1677ff",
            },
          },

          "& input": {
            fontWeight: 700,
            color: "#0f172a",
          },
        }}
      />
    </Box>
  );
}

export default function UserPage() {
  const user = React.useMemo(() => getStoredUser(), []);
  const currentUser = user?.results || {};

  const firstName = currentUser.firstName || "";
  const lastName = currentUser.lastName || "";
  const email = currentUser.email || "";
  const branch = currentUser.branch || "";
  const role = currentUser.userRole || "";
  const createdAt = currentUser.createdAt;

  const fullName = `${firstName} ${lastName}`.trim() || "Admin User";
  const initials = getInitials(firstName, lastName);

  const joinedDisplay = createdAt
    ? `${moment(createdAt).format("DD MMM YYYY")} | ${moment(createdAt).fromNow()}`
    : "—";

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: "hidden",
          boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
          border: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1.6} alignItems="center">
              <Box
                sx={{
                  p: "3px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #1677ff 0%, #22c55e 100%)",
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    fontWeight: 950,
                    fontSize: 22,
                    background:
                      "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
                    border: "4px solid #fff",
                  }}
                >
                  {initials}
                </Avatar>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: 950,
                    fontSize: { xs: 21, md: 25 },
                    lineHeight: 1.1,
                  }}
                >
                  {fullName}
                </Typography>

                <Typography
                  sx={{
                    color: "#cbd5e1",
                    fontSize: 13,
                    mt: 0.5,
                  }}
                >
                  {email || "No email available"}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mt: 1 }}
                >
                  {role && (
                    <Chip
                      size="small"
                      icon={<VerifiedUserRoundedIcon />}
                      label={role}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 900,
                        color: "#bfdbfe",
                        background: "rgba(22, 119, 255, .18)",
                        border: "1px solid rgba(147,197,253,.24)",
                        "& .MuiChip-icon": {
                          color: "#93c5fd",
                        },
                      }}
                    />
                  )}

                  {branch && (
                    <Chip
                      size="small"
                      label={branch}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 900,
                        color: "#bbf7d0",
                        background: "rgba(34, 197, 94, .14)",
                        border: "1px solid rgba(134,239,172,.24)",
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Button
              variant="contained"
              startIcon={<EditRoundedIcon />}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 900,
                background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
                boxShadow: "0 10px 20px rgba(22, 119, 255, .25)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0958d9 0%, #1677ff 100%)",
                },
              }}
            >
              Update Info
            </Button>
          </Stack>
        </Box>

        {/* Body */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 3,
                background: "#eff6ff",
                color: "#1d4ed8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PersonRoundedIcon />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: "#0f172a",
                  fontSize: 18,
                  lineHeight: 1.1,
                }}
              >
                User Information
              </Typography>

              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: 12,
                  mt: 0.3,
                }}
              >
                These account details are read-only.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InfoField
                label="First Name"
                value={firstName}
                icon={<PersonRoundedIcon sx={{ fontSize: 16 }} />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Last Name"
                value={lastName}
                icon={<PersonRoundedIcon sx={{ fontSize: 16 }} />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Email"
                value={email}
                icon={<EmailRoundedIcon sx={{ fontSize: 16 }} />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Branch"
                value={branch}
                icon={<BusinessRoundedIcon sx={{ fontSize: 16 }} />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Role"
                value={role}
                icon={<AdminPanelSettingsRoundedIcon sx={{ fontSize: 16 }} />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Joined At"
                value={joinedDisplay}
                icon={<CalendarMonthRoundedIcon sx={{ fontSize: 16 }} />}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}
